import React, { useState } from "react";
import { useMutation } from "react-query";
import {
  requestMagicLink,
  resetPasswordRequest,
} from "../services/auth.service";
import Lottie from "lottie-react";
import emailAnimation from "./../assets/email-magik.json";
import Button from "./button";
import { ButtonSize, ButtonVariant } from "./button/types";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../assets/close.svg";
import { ClassNames } from "@emotion/react";

interface RecoveryDialogProps {
  open: boolean;
  onClose: () => void;
  email: string;
}

const RecoveryDialog: React.FC<RecoveryDialogProps> = ({
  open,
  onClose,
  email,
}) => {
  const [currentView, setCurrentView] = useState<
    "options" | "reset_sent" | "magic_link_sent"
  >("options");
  const [internalError, setInternalError] = useState<string | null>(null);
  const { t } = useTranslation("authentication");

  const { mutate: sendResetEmail, isLoading: isResetLoading } = useMutation(
    () => resetPasswordRequest({ email }),
    {
      onSuccess: (data: any) => {
        setCurrentView("reset_sent");
        setInternalError(null);
      },
      onError: (err: any) => {
        setInternalError(
          t("errors.failed-send-password-reset") ||
            "Failed to send password reset email."
        );
      },
    }
  );

  const { mutate: sendMagicLink, isLoading: isMagicLinkLoading } = useMutation(
    () => requestMagicLink({ email }),
    {
      onSuccess: (data: any) => {
        setCurrentView("magic_link_sent");
        setInternalError(null);
      },
      onError: (err: any) => {
        setInternalError(
          t("errors.failed-send-magic") || "Failed to send magic link."
        );
      },
    }
  );

  const handleCloseAndResetDialog = () => {
    setCurrentView("options");
    setInternalError(null);
    onClose();
  };

  const isLoading = isResetLoading || isMagicLinkLoading;

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white p-6 lg:rounded-lg shadow-lg lg:w-1/3 w-full h-full lg:h-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            {currentView === "reset_sent"
              ? `${t("password-reset-email-sent")}`
              : currentView === "magic_link_sent"
              ? `${t("magic-linc-sent")}`
              : `${t("magic-linc-title")}`}
          </h2>
          <CloseIcon
            onClick={handleCloseAndResetDialog}
            className="cursor-pointer"
          />
        </div>

        <div className="">
          {currentView === "options" && (
            <>
              <p className="mb-4">{t("magic-linc-description")}</p>
              <div>
                <input
                  type="email"
                  id="recovery-email"
                  value={email}
                  disabled
                  className="mt-1 block w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-md shadow-sm cursor-not-allowed "
                />
              </div>
            </>
          )}

          {currentView === "reset_sent" && (
            <div>
              <p className="mb-2">
                {t("reset-sent-description-part1")}{" "}
                <strong className="font-medium">{email}</strong>{" "}
                {t("reset-sent-description-part2")}
              </p>
              <div className="flex justify-center">
                <Lottie
                  animationData={emailAnimation}
                  loop={false}
                  style={{ width: "141px", height: "140px" }}
                />
              </div>
              <p className="text-center">{t("reset-sent-valid")}</p>
            </div>
          )}

          {currentView === "magic_link_sent" && (
            <div>
              <p className="mb-2">
                {t("magic-linc-sent-description-part1")}{" "}
                <strong className="font-medium">{email}</strong>{" "}
                {t("magic-linc-sent-description-part2")}
              </p>
              <div className="flex justify-center">
                <Lottie
                  animationData={emailAnimation}
                  loop={false}
                  style={{ width: "141px", height: "140px" }}
                />
              </div>
              <p className="text-center">{t("magic-linc-sent-valid")}</p>
            </div>
          )}
        </div>

        {currentView === "options" && (
          <div className="flex items-center justify-between mt-6">
            <Button
              className="w-full mr-2"
              onClick={() => sendResetEmail()}
              variant={ButtonVariant.SECONDARY}
              size={ButtonSize.SMALL}
            >
              {t("reset-password")}
            </Button>

            <Button
              className="w-full ml-2"
              onClick={() => sendMagicLink()}
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SMALL}
            >
              {t("send-magic-linc")}
            </Button>
          </div>
        )}
        {internalError && (
          <div className="mt-2 text-sm text-red-600 flex justify-end">
            {internalError}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecoveryDialog;
